import type { Course } from '@/mocks/courses';

interface CourseCardProps {
  course: Course;
}

export default function CourseCard({ course }: CourseCardProps) {
  return (
    <div className="flex flex-col h-full">
      <div className="gap-6 rounded-xl border border-background-200 bg-background-50 text-foreground-900 shadow-sm group overflow-hidden hover:shadow-md transition-shadow duration-300 pt-0 flex flex-col h-full">
        {/* Image */}
        <div className="relative overflow-hidden">
          <img
            alt={course.title}
            className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500"
            src={course.image}
          />
          <div className="absolute top-3 left-3">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${course.difficultyColor}`}>
              {course.difficulty}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 font-medium whitespace-nowrap transition-colors bg-secondary-100 text-secondary-900 text-xs">
              {course.category}
            </span>
          </div>

          <h3 className="font-bold text-base leading-snug mb-2 group-hover:text-primary-600 transition-colors font-heading">
            {course.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 mb-4">{course.description}</p>

          <div className="flex items-center gap-4 text-xs text-foreground-500 mb-4 flex-wrap">
            <span className="flex items-center gap-1">
              <i className="ri-time-line w-3 h-3 flex items-center justify-center" />
              {course.hours}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-book-open-line w-3 h-3 flex items-center justify-center" />
              {course.lessons}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line w-3 h-3 flex items-center justify-center" />
              {course.students}
            </span>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <i
                key={i}
                className={`ri-star-fill text-xs w-3 h-3 flex items-center justify-center ${
                  i < Math.floor(parseFloat(course.rating)) ? 'text-primary-400' : 'text-background-300'
                }`}
              />
            ))}
            <span className="text-xs text-foreground-500 ml-1">{course.rating}</span>
          </div>

          {/* Price & CTA */}
          <div className="mt-auto flex items-center justify-between">
            <span className="text-2xl font-extrabold text-foreground-950">{course.price}</span>
            <Link
              to={course.slug}
              className="inline-flex shrink-0 items-center justify-center text-sm font-medium whitespace-nowrap transition-all bg-primary-500 text-background-50 hover:bg-primary-600 h-8 gap-1.5 rounded-md px-3"
            >
              View Course
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}